import { InsolvenciesFormModal } from "./insolvenciesFormModal";
import { useMemo, useRef, useState } from "react";
import type { RegistryType } from "@/schemas/digital-files";
import type { InsolvencyType } from "@/types/entities/insolvencies";
import { InsolvencyBanner } from "@/components/insolvencies/insolvency-banner";
import InsolvencyListModal from "@/components/insolvencies/insolvency-list-modal";
import { InsolvencyCancelModal } from "@/components/insolvencies/insolvency-cancel-modal";

export type TypeModalState =
  | "idle"
  | "insolvency-form"
  | "insolvency-list"
  | "insolvency-form-list"
  | "insolvency-cancel";

interface ExpedientRegistryInsolvenciesProps {
  modalState: string;
  setModalState: (state: TypeModalState) => void;
  registry?: RegistryType;
}
export const ExpedientRegistryInsolvencies = ({
  registry,
  modalState,
  setModalState,
}: ExpedientRegistryInsolvenciesProps) => {
  const [selectedInsolvency, setSelectedInsolvency] = useState<
    InsolvencyType | undefined
  >(undefined);
  const rejectAppealResolverRef = useRef<
    ((comment: string | null) => void) | null
  >(null);

  const showBanner = useMemo(
    () =>
      registry?.unpaid_insolvencies_count &&
      registry.unpaid_insolvencies_count > 0,
    [registry?.unpaid_insolvencies_count],
  );

  const openFormModal = () =>
    setModalState(
      modalState === "insolvency-list"
        ? "insolvency-form-list"
        : "insolvency-form",
    );
  const openListModal = () => setModalState("insolvency-list");
  const closeModal = () =>
    setModalState(
      modalState === "insolvency-form-list" ? "insolvency-list" : "idle",
    );

  const openUpdateModal = (item: InsolvencyType) => {
    setSelectedInsolvency(item);
    openFormModal();
  };

  const openRejectAppealModal = (item: InsolvencyType) => {
    setSelectedInsolvency(item);
    setModalState("insolvency-cancel");

    return new Promise<string | null>((resolve) => {
      rejectAppealResolverRef.current = resolve;
    });
  };

  const handleRejectAppeal = async (item: InsolvencyType) => {
    const comment = await openRejectAppealModal(item);

    if (comment === null) {
      return;
    }
  };

  const resolveRejectAppeal = (comment: string | null) => {
    rejectAppealResolverRef.current?.(comment);
    rejectAppealResolverRef.current = null;
    setSelectedInsolvency(undefined);
    setModalState("insolvency-list");
  };

  return (
    <>
      {showBanner ? (
        <InsolvencyBanner
          count={registry?.unpaid_insolvencies_count}
          onClick={openListModal}
        />
      ) : null}

      <InsolvenciesFormModal
        isOpen={
          modalState === "insolvency-form" ||
          modalState === "insolvency-form-list"
        }
        insolvencyData={selectedInsolvency}
        onClose={closeModal}
        registry={registry}
      />
      <InsolvencyListModal
        registry={registry}
        isOpen={
          modalState === "insolvency-list" ||
          modalState === "insolvency-form-list" ||
          modalState === "insolvency-cancel"
        }
        onClose={closeModal}
        showActions
        onAdd={openFormModal}
        onUpdate={openUpdateModal}
        onRejectAppeal={(item) => handleRejectAppeal(item)}
      />

      <InsolvencyCancelModal
        isOpen={modalState === "insolvency-cancel"}
        infractionId={selectedInsolvency?.id || null}
        reason={selectedInsolvency?.reason || ""}
        onClose={() => setModalState("insolvency-list")}
        onResolve={resolveRejectAppeal}
      />
    </>
  );
};
